/**
 * ImageStorageService — Firebase Storage upload utilities for web
 *
 * Provides helpers to:
 * 1. Upload a raw URL (fetched via proxy or cors) to Firebase Storage
 * 2. Upload Google Maps JS-API photo objects (p.getURI()) to Firebase Storage
 * 3. Detect whether a URL is already a permanent Firebase Storage URL
 */
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';
import { ApiClient } from './api-client.service';

export class ImageStorageService {
    /**
     * Picks a representative destination photo and stores it as the trip's
     * highlight image, so a freshly created trip is never image-less.
     *
     * Shared by the AI planner and the manual trip dialog. Runs async after
     * trip creation and never throws — this is a nice-to-have.
     */
    static async addAutoTripHighlightImage(tripId: string, countryName: string): Promise<void> {
        try {
            const g = (window as any).google?.maps?.places;
            if (!g) return;
            const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
            if (!apiKey) return;

            const { Place } = g;
            const { places } = await Place.searchByText({
                textQuery: `${countryName} travel attractions landmarks`,
                fields: ['id', 'displayName', 'photos'],
                includedType: 'tourist_attraction',
                maxResultCount: 5,
            });

            let photoName: string | null = null;
            for (const place of (places ?? [])) {
                if (place.photos && place.photos.length > 0 && place.photos[0].name) {
                    photoName = place.photos[0].name;
                    break;
                }
            }
            if (!photoName) return;

            const url = await this.uploadGooglePlacesPhotoByRef(photoName, `trips/${tripId}/highlights`);
            if (url) {
                // Imported lazily to keep this module free of a service cycle.
                const { default: TripService } = await import('./trip.service');
                await TripService.updateTrip(tripId, { highlightImages: [url], mainImageIndex: 0 });
                console.log('✅ [ImageStorage] Auto highlight image set:', url);
            }
        } catch (e) {
            console.warn('[ImageStorage] Could not add auto highlight image:', e);
        }
    }

    /**
     * Returns true if the URL is already a permanent Firebase Storage URL.
     */
    static isFirebaseStorageUrl(url: string): boolean {
        return (
            url.includes('firebasestorage.googleapis.com') ||
            url.includes('storage.googleapis.com')
        );
    }

    /**
     * Returns true if the URL looks like a temporary Google Places / Maps JS API URL.
     * These expire and should always be replaced with a stored copy.
     */
    static isGooglePlacesUrl(url: string): boolean {
        return (
            url.includes('places.googleapis.com') ||
            url.includes('maps.googleapis.com/maps/api/place/photo') ||
            url.includes('lh3.googleusercontent.com') ||
            url.includes('googleusercontent.com')
        );
    }

    /**
     * Fetch an image URL and upload the bytes to Firebase Storage.
     * Returns the permanent download URL, or null on failure.
     *
     * @param imageUrl  The source URL (must be CORS-accessible or a blob URL).
     * @param storagePath  Full Firebase Storage path (e.g. "trips/abc/activities/xyz").
     * @param fileName  Optional filename; defaults to a UUID + .jpg.
     */
    static async uploadImageFromUrl(
        imageUrl: string,
        storagePath: string,
        fileName?: string
    ): Promise<string | null> {
        try {
            console.log('📤 [ImageStorage] Uploading image from URL:', imageUrl.substring(0, 80) + '...');

            // Fetch the image bytes
            const response = await fetch(imageUrl, { mode: 'cors' });
            if (!response.ok) {
                console.warn('⚠️ [ImageStorage] Failed to fetch image, status:', response.status);
                return null;
            }

            const blob = await response.blob();
            if (blob.size === 0) {
                console.warn('⚠️ [ImageStorage] Empty blob received');
                return null;
            }

            // Use provided name or generate one
            const name = fileName ?? `${crypto.randomUUID()}.jpg`;
            const fullPath = `${storagePath}/${name}`;

            const storageRef = ref(storage, fullPath);
            await uploadBytes(storageRef, blob, {
                contentType: blob.type || 'image/jpeg',
            });

            const downloadUrl = await getDownloadURL(storageRef);
            console.log('✅ [ImageStorage] Stored image at:', fullPath);
            return downloadUrl;
        } catch (err) {
            console.error('❌ [ImageStorage] Error uploading image from URL:', err);
            return null;
        }
    }

    /**
     * Upload a single Google Places photo to Firebase Storage using the API gateway photo proxy.
     *
     * The gateway proxies the Places photo binary — no API key needed client-side.
     *
     * @param photoName   Google Places photo resource name, e.g. "places/ChIJ.../photos/..."
     * @param storagePath Firebase Storage folder path.
     * @param fileName    Optional filename; a UUID.jpg is used when omitted.
     */
    static async uploadGooglePlacesPhotoByRef(
        photoName: string,
        storagePath: string,
        fileName?: string
    ): Promise<string | null> {
        console.log('📤 [ImageStorage] uploadGooglePlacesPhotoByRef → photoName:', photoName, '→ storagePath:', storagePath);
        try {
            // Use the gateway's public photo proxy (no auth required)
            const params = new URLSearchParams({
                photoReference: photoName,
                maxHeight: '800',
                maxWidth: '800',
            });
            const response = await fetch(`${ApiClient.baseUrl}/places/photo?${params}`);

            if (!response.ok) {
                console.warn('⚠️ [ImageStorage] Gateway photo proxy returned:', response.status, await response.text());
                return null;
            }

            const imageBlob = await response.blob();
            if (!imageBlob || imageBlob.size === 0) {
                console.warn('⚠️ [ImageStorage] Empty blob from gateway photo proxy');
                return null;
            }

            const name = fileName ?? `${crypto.randomUUID()}.jpg`;
            const storageRef = ref(storage, `${storagePath}/${name}`);
            await uploadBytes(storageRef, imageBlob, { contentType: imageBlob.type || 'image/jpeg' });
            const downloadUrl = await getDownloadURL(storageRef);
            console.log('✅ [ImageStorage] Stored Places photo at:', `${storagePath}/${name}`);
            return downloadUrl;
        } catch (err) {
            console.error('❌ [ImageStorage] Error uploading Places photo by ref:', err);
            return null;
        }
    }

    /**
     * Takes an array of Google Maps JS-API photo objects (from place.photos),
     * uploads each to Firebase Storage, and returns permanent Firebase download URLs.
     *
     * Preferred path: use `photo.name` (resource path) + Places REST API with
     * `skipHttpRedirect=true` to avoid CORS issues when fetching photo media.
     * Falls back to `photo.getURI()` + direct fetch when no apiKey is provided.
     *
     * @param photos        Array of Maps JS API photo objects (must have .name and/or .getURI()).
     * @param storagePath   Firebase Storage folder path.
     * @param maxPhotos     Maximum number of photos to process (default 3).
     * @param apiKey        Google Maps API key — required for the preferred REST path.
     */
    static async uploadGooglePlacesPhotos(
        photos: any[],
        storagePath: string,
        maxPhotos: number = 3,
    ): Promise<string[]> {
        if (!photos || photos.length === 0) return [];

        const slice = photos.slice(0, maxPhotos);
        const results: string[] = [];

        for (const photo of slice) {
            try {
                let firebaseUrl: string | null = null;

                // Preferred: use photo.name + gateway photo proxy
                if (photo.name) {
                    firebaseUrl = await this.uploadGooglePlacesPhotoByRef(
                        photo.name, storagePath,
                    );
                }

                // Fallback: getURI() + direct fetch (may hit CORS in some browsers)
                if (!firebaseUrl && photo.getURI) {
                    const uri: string = photo.getURI({ maxWidth: 800, maxHeight: 600 });
                    firebaseUrl = await this.uploadImageFromUrl(uri, storagePath);
                }

                if (firebaseUrl) {
                    results.push(firebaseUrl);
                }
            } catch (err) {
                console.warn('⚠️ [ImageStorage] Skipping photo, error:', err);
            }
        }

        console.log(`✅ [ImageStorage] Uploaded ${results.length}/${slice.length} photos to ${storagePath}`);
        return results;
    }

    /**
     * Ensure that every URL in `images` is a permanent Firebase Storage URL.
     *
     * - Firebase Storage URLs → kept as-is.
     * - Google Places / Maps CDN URLs → skipped (they expire and cannot be fetched
     *   via CORS from a browser; they should have been uploaded during place selection).
     * - Any other URL → attempt to download and re-upload; kept as-is only if it
     *   is NOT a Google-owned URL (to avoid storing expired references).
     *
     * @param images       Array of image URL strings.
     * @param storagePath  Firebase Storage folder path.
     */
    static async ensureStoredImages(
        images: string[],
        storagePath: string
    ): Promise<string[]> {
        if (!images || images.length === 0) return [];

        const results: string[] = [];

        for (const url of images) {
            if (this.isFirebaseStorageUrl(url)) {
                // Already a permanent Firebase Storage URL — keep as-is
                results.push(url);
            } else if (this.isGooglePlacesUrl(url)) {
                // Temporary Google Places / CDN URL — cannot be reliably fetched
                // from the browser due to CORS.  These should have been uploaded to
                // Firebase Storage already during the place-selection step.
                // Drop them here to avoid storing expired / invalid URLs in Firestore.
                console.warn(
                    '⚠️ [ImageStorage] Dropping Google Places URL (not uploaded during selection):',
                    url.substring(0, 100)
                );
            } else {
                // Non-Google external URL — attempt to upload to Firebase Storage
                const stored = await this.uploadImageFromUrl(url, storagePath);
                if (stored) {
                    results.push(stored);
                } else {
                    // Keep the original only if it is a user-provided non-Google URL
                    results.push(url);
                }
            }
        }

        return results;
    }

    /**
     * Re-fetch a place's photos from the Google Places New API by place name/ID
     * and upload them to Firebase Storage.
     *
     * NOTE: This uses the standard REST Places API (not the Maps JS API),
     * so it works in service context without a DOM.
     *
     * @param placeName     Display name of the place (used for the text search).
     * @param storagePath   Firebase Storage folder path.
     * @param apiKey        Google Maps API key.
     */
    static async refreshPlaceImagesByName(
        placeName: string,
        storagePath: string,
    ): Promise<string[]> {
        try {
            console.log('🔄 [ImageStorage] Refreshing images for place:', placeName);

            // Use the gateway's Places search proxy
            const searchData = await ApiClient.post<{ places?: any[] }>('/places/search', {
                textQuery: placeName,
                fieldMask: 'places.id,places.displayName,places.photos',
            });

            const place = searchData.places?.[0];
            if (!place?.photos?.length) {
                console.warn('⚠️ [ImageStorage] No photos found for:', placeName);
                return [];
            }

            // Fetch each photo via the gateway photo proxy
            const photoRefs: string[] = place.photos.slice(0, 3).map((p: any) => p.name);
            const results: string[] = [];

            for (const photoName of photoRefs) {
                try {
                    const stored = await this.uploadGooglePlacesPhotoByRef(photoName, storagePath);
                    if (stored) results.push(stored);
                } catch (e) {
                    console.warn('⚠️ [ImageStorage] Error uploading photo:', e);
                }
            }

            console.log(`✅ [ImageStorage] Refreshed ${results.length} images for "${placeName}"`);
            return results;
        } catch (err) {
            console.error('❌ [ImageStorage] Error refreshing place images:', err);
            return [];
        }
    }
}

export default ImageStorageService;
